/**
 * Invoice Page - View and pay invoices
 * Premium dark/gold design matching RC Studios branding
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { InvoiceSendControls } from '@/components/admin/InvoiceSendControls';
import { authFetch } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Loader2, Download, CreditCard, CheckCircle, 
  AlertCircle, FileText, ArrowLeft, ExternalLink, Star 
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Company info
const COMPANY_INFO = {
  name: "RCSTUDIOS LLC",
  address: "Kingman, AZ",
  phone: "(928) 530-6604",
  email: "marketing@rcstudios.net",
};

export default function InvoicePage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [reviewSettings, setReviewSettings] = useState({ google_review_link: '', review_enabled: false });
  
  // Check for payment success/cancel from URL params
  const sessionId = searchParams.get('session_id');
  const paidParam = searchParams.get('paid') === 'true';
  const paymentCancelled = searchParams.get('cancelled') === 'true';
  
  // Payment is truly successful only when invoice status is 'paid'
  const isPaid = invoice?.status === 'paid';
  
  // Track if we're in payment verification flow
  const isPaymentReturn = sessionId || paidParam;

  // Timeout handler for loading states
  useEffect(() => {
    let timeoutId;
    if (loading || (verifyingPayment && !paymentVerified)) {
      timeoutId = setTimeout(() => {
        setLoadingTimeout(true);
      }, 15000); // 15 second timeout
    } else {
      setLoadingTimeout(false);
    }
    return () => clearTimeout(timeoutId);
  }, [loading, verifyingPayment, paymentVerified]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchInvoice();
    }
  }, [invoiceId, authLoading, isAuthenticated]);

  // Fetch review settings
  useEffect(() => {
    const fetchReviewSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/settings/review-link`);
        if (response.ok) {
          const data = await response.json();
          setReviewSettings(data);
        }
      } catch (err) {
        console.error('Failed to fetch review settings:', err);
      }
    };
    fetchReviewSettings();
  }, []);

  // Verify payment when redirected back from Stripe
  useEffect(() => {
    if (isPaymentReturn && invoice && !isPaid && !verifyingPayment && !paymentVerified && verifyAttempts < 5) {
      verifyPayment();
    }
  }, [sessionId, paidParam, invoice, isPaid, verifyingPayment, paymentVerified, verifyAttempts]);

  const fetchInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Invoice not found');
        } else if (response.status === 403) {
          setError('You do not have access to this invoice');
        } else {
          setError('Failed to load invoice');
        }
        return;
      }
      const data = await response.json();
      setInvoice(data);
      setLoadingTimeout(false);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    setVerifyingPayment(true);
    setVerifyAttempts(prev => prev + 1);
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.paid) {
          setPaymentVerified(true);
          setLoadingTimeout(false);
          // Refresh invoice to get updated status
          await fetchInvoice();
          // Clear URL params
          setSearchParams({});
        } else if (verifyAttempts < 4) {
          // Payment not yet confirmed - poll again after delay
          setTimeout(() => {
            setVerifyingPayment(false);
          }, 3000);
        } else {
          // Max attempts reached
          setVerifyingPayment(false);
          setLoadingTimeout(true);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Verify payment error:', errorData);
        setVerifyingPayment(false);
        if (verifyAttempts >= 4) {
          setError(errorData.detail || 'Could not verify payment. Please refresh the page or contact support.');
        }
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      setVerifyingPayment(false);
      if (verifyAttempts >= 4) {
        setError('Network error while verifying payment. Please refresh the page.');
      } else {
        setTimeout(() => {
          setVerifyingPayment(false);
        }, 3000);
      }
    }
  };

  const handleRetry = () => {
    setLoadingTimeout(false);
    setError(null);
    setVerifyAttempts(0);
    if (isPaymentReturn) {
      setVerifyingPayment(false);
      // Will trigger verifyPayment via useEffect
    } else {
      fetchInvoice();
    }
  };

  const handlePayInvoice = async () => {
    setPaymentLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout (or mock page)
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      setPaymentLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoiceId}/pdf`);
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice?.invoice_number || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Loading state with labels and timeout handling
  if (authLoading || loading) {
    // Show payment-specific message if returning from Stripe
    const loadingMessage = isPaymentReturn ? 'Processing your payment...' : 'Loading invoice...';
    
    if (loadingTimeout) {
      return (
        <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
          <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
          <h1 className="text-xl text-white mb-2">This is taking longer than usual</h1>
          <p className="text-[#E8E4DE]/60 mb-6 text-center max-w-md">
            {isPaymentReturn 
              ? "We're still confirming your payment. Your payment may have succeeded - please check your email for confirmation."
              : "There might be a connection issue. Please try again."}
          </p>
          <div className="flex gap-3">
            <Button onClick={handleRetry} className="bg-[#C6A14A] text-black hover:bg-[#d4af5a]">
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/orders')} className="border-white/20 text-[#E8E4DE]">
              Back to Orders
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin mb-4" />
        <p className="text-[#E8E4DE]/60">{loadingMessage}</p>
      </div>
    );
  }

  // Auth required
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">Sign In Required</h1>
        <p className="text-[#E8E4DE]/60 mb-6">Please sign in to view this invoice</p>
        <Button onClick={() => navigate('/login')}>Sign In</Button>
      </div>
    );
  }

  // Error state with retry option
  if (error) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h1 className="text-2xl text-white mb-4">Error</h1>
        <p className="text-[#E8E4DE]/60 mb-6 text-center max-w-md">{error}</p>
        <div className="flex gap-3">
          <Button onClick={handleRetry} className="bg-[#C6A14A] text-black hover:bg-[#d4af5a]">
            Try Again
          </Button>
          <Button variant="outline" onClick={() => navigate('/orders')} className="border-white/20 text-[#E8E4DE]">
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  // Verifying payment state (after returning from Stripe)
  if (verifyingPayment && !invoice) {
    if (loadingTimeout) {
      return (
        <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
          <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
          <h1 className="text-xl text-white mb-2">This is taking longer than usual</h1>
          <p className="text-[#E8E4DE]/60 mb-6 text-center max-w-md">
            We're still confirming your payment. Your payment may have succeeded - please check your email for confirmation.
          </p>
          <div className="flex gap-3">
            <Button onClick={handleRetry} className="bg-[#C6A14A] text-black hover:bg-[#d4af5a]">
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/orders')} className="border-white/20 text-[#E8E4DE]">
              Back to Orders
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin mb-4" />
        <p className="text-[#E8E4DE]/60">Confirming payment...</p>
      </div>
    );
  }

  if (!invoice) {
    if (loadingTimeout) {
      return (
        <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
          <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
          <h1 className="text-xl text-white mb-2">This is taking longer than usual</h1>
          <p className="text-[#E8E4DE]/60 mb-6">There might be a connection issue.</p>
          <div className="flex gap-3">
            <Button onClick={handleRetry} className="bg-[#C6A14A] text-black hover:bg-[#d4af5a]">
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/orders')} className="border-white/20 text-[#E8E4DE]">
              Back to Orders
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin mb-4" />
        <p className="text-[#E8E4DE]/60">Loading...</p>
      </div>
    );
  }

  const isDraft = invoice.status === 'draft';

  return (
    <div className="min-h-screen bg-[#050816] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          className="mb-6 text-[#E8E4DE]/60 hover:text-white"
          onClick={() => navigate('/orders')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>

        {/* Payment Success Message - only show when actually paid */}
        {isPaid && (
          <Card className="bg-green-500/10 border-green-500/30 mb-6">
            <CardContent className="py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-400">Payment successful! Your downloads are now unlocked.</span>
              </div>
              {reviewSettings.review_enabled && reviewSettings.google_review_link && (
                <a
                  href={reviewSettings.google_review_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#C6A14A] text-[#050816] px-4 py-2 rounded-lg hover:bg-[#D4AF5A] transition-colors font-medium text-sm whitespace-nowrap"
                  data-testid="google-review-btn"
                >
                  <Star className="h-4 w-4" />
                  Leave a Review
                </a>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Verifying payment message - show when redirected back but not yet confirmed */}
        {isPaymentReturn && !isPaid && (
          <Card className={`${loadingTimeout ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-blue-500/10 border-blue-500/30'} mb-6`}>
            <CardContent className="py-4">
              {loadingTimeout ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-yellow-400">This is taking longer than usual. Your payment may have succeeded.</span>
                  </div>
                  <Button size="sm" onClick={handleRetry} className="bg-[#C6A14A] text-black hover:bg-[#d4af5a]">
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                  <span className="text-blue-400">Confirming payment... This may take a moment.</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {user?.roles?.includes('admin') && invoice && invoice.status !== 'draft' && (
          <Card className="bg-[#0f1724] border-[#C6A14A]/30 mb-6" data-testid="admin-invoice-tools">
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wider text-[#C6A14A] mb-3">Admin tools</p>
              <InvoiceSendControls invoice={invoice} onUpdated={(d) => setInvoice({ ...invoice, ...d })} />
            </CardContent>
          </Card>
        )}

        {paymentCancelled && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 mb-6">
            <CardContent className="py-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <span className="text-yellow-400">Payment was cancelled. You can try again when ready.</span>
            </CardContent>
          </Card>
        )}

        {/* Invoice Card */}
        <Card className="bg-[#0a0a0f] border-[#C6A14A]/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#12121a] to-[#0a0a0f] p-8 border-b border-[#C6A14A]/20">
            <div className="flex justify-between items-start">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png" 
                  alt="RC Studios" 
                  className="h-12 mb-3"
                />
                <p className="text-[#E8E4DE]/60 text-sm">
                  {COMPANY_INFO.address}<br />
                  {COMPANY_INFO.phone}<br />
                  {COMPANY_INFO.email}
                </p>
              </div>
              <div className="text-right">
                <Badge 
                  className={`mb-3 ${
                    isPaid 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                      : isDraft
                      ? 'bg-white/10 text-[#E8E4DE]'
                      : 'bg-[#C6A14A]/20 text-[#C6A14A] border-[#C6A14A]/30'
                  }`}
                >
                  {isPaid ? 'PAID' : isDraft ? 'DRAFT' : 'UNPAID'}
                </Badge>
                <h2 className="text-2xl text-white font-light">INVOICE</h2>
                <p className="text-[#C6A14A] text-lg font-medium mt-1">
                  {invoice.invoice_number}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <CardContent className="p-8">
            {/* Bill To & Dates */}
            <div className="flex justify-between mb-8">
              <div>
                <h4 className="text-[#C6A14A] text-xs tracking-wider mb-2">BILL TO</h4>
                {invoice.customer_name && invoice.customer_name !== 'None' ? (
                  <p className="text-white font-medium">{invoice.customer_name}</p>
                ) : null}
                {invoice.customer_company && (
                  <p className="text-[#E8E4DE]/70">{invoice.customer_company}</p>
                )}
                {invoice.customer_email && (
                  <p className="text-[#E8E4DE]/70">{invoice.customer_email}</p>
                )}
                {invoice.customer_phone && (
                  <p className="text-[#E8E4DE]/70">{invoice.customer_phone}</p>
                )}
                {!invoice.customer_name && !invoice.customer_company && !invoice.customer_email && (
                  <p className="text-[#E8E4DE]/50">Customer information not available</p>
                )}
              </div>
              <div className="text-right">
                <div className="mb-3">
                  <span className="text-[#E8E4DE]/50 text-xs">Issue Date</span><br />
                  <span className="text-white">{invoice.issue_date?.split('T')[0]}</span>
                </div>
                <div>
                  <span className="text-[#E8E4DE]/50 text-xs">Due Date</span><br />
                  <span className="text-white">{invoice.due_date?.split('T')[0]}</span>
                </div>
              </div>
            </div>

            {/* Property - only show if available */}
            {invoice.property_address && invoice.property_address !== 'None' && (
              <div className="bg-[#C6A14A]/10 rounded-lg p-4 mb-8">
                <span className="text-[#C6A14A] text-xs tracking-wider">PROPERTY</span><br />
                <span className="text-white">{invoice.property_address}</span>
              </div>
            )}

            {/* Line Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-[#C6A14A]/30">
                  <th className="py-3 text-left text-[#C6A14A] text-xs tracking-wider">DESCRIPTION</th>
                  <th className="py-3 text-center text-[#C6A14A] text-xs tracking-wider">QTY</th>
                  <th className="py-3 text-right text-[#C6A14A] text-xs tracking-wider">RATE</th>
                  <th className="py-3 text-right text-[#C6A14A] text-xs tracking-wider">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-white/10">
                    <td className="py-4 text-[#E8E4DE]">{item.description}</td>
                    <td className="py-4 text-center text-[#E8E4DE]">{item.quantity}</td>
                    <td className="py-4 text-right text-[#E8E4DE]">
                      ${(item.unit_price_cents / 100).toFixed(2)}
                    </td>
                    <td className="py-4 text-right text-[#E8E4DE]">
                      ${((item.quantity * item.unit_price_cents) / 100).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {invoice.addons?.map((addon, idx) => (
                  <tr key={`addon-${idx}`} className="border-b border-white/10">
                    <td className="py-4 text-[#E8E4DE]">{addon.name}</td>
                    <td className="py-4 text-center text-[#E8E4DE]">1</td>
                    <td className="py-4 text-right text-[#E8E4DE]">
                      ${(addon.price_cents / 100).toFixed(2)}
                    </td>
                    <td className="py-4 text-right text-[#E8E4DE]">
                      ${(addon.price_cents / 100).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="py-3 text-right text-[#E8E4DE]/70">Subtotal</td>
                  <td className="py-3 text-right text-[#E8E4DE]">
                    ${(invoice.subtotal_cents / 100).toFixed(2)}
                  </td>
                </tr>
                {invoice.tax_cents > 0 && (
                  <tr>
                    <td colSpan="3" className="py-2 text-right text-[#E8E4DE]/70">
                      Tax ({(invoice.tax_rate * 100).toFixed(1)}%)
                    </td>
                    <td className="py-2 text-right text-[#E8E4DE]">
                      ${(invoice.tax_cents / 100).toFixed(2)}
                    </td>
                  </tr>
                )}
                {invoice.discount_cents > 0 && (
                  <tr data-testid="invoice-discount-row">
                    <td colSpan="3" className="py-2 text-right text-green-400">
                      Discount{invoice.discount_code ? ` (${invoice.discount_code})` : ''}
                    </td>
                    <td className="py-2 text-right text-green-400">
                      -${(invoice.discount_cents / 100).toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr className="border-t-2 border-[#C6A14A]/30">
                  <td colSpan="3" className="py-4 text-right text-[#C6A14A] text-xl font-semibold">
                    {invoice.discount_cents > 0 ? 'TOTAL PAID' : 'TOTAL'}
                  </td>
                  <td className="py-4 text-right text-[#C6A14A] text-2xl font-semibold" data-testid="invoice-total">
                    ${((invoice.discount_cents > 0 && invoice.amount_paid_cents != null ? invoice.amount_paid_cents : invoice.total_cents) / 100).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* Notes */}
            {invoice.notes && (
              <div className="bg-[#C6A14A]/10 rounded-lg p-4 mb-8">
                <h4 className="text-[#C6A14A] text-sm mb-2">Notes</h4>
                <p className="text-[#E8E4DE]/80 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              {!isPaid && !isDraft && (
                <Button
                  size="lg"
                  className="bg-[#C6A14A] hover:bg-[#d4af5a] text-black font-semibold px-8"
                  onClick={handlePayInvoice}
                  disabled={paymentLoading || verifyingPayment}
                  data-testid="pay-invoice-btn"
                >
                  {paymentLoading || verifyingPayment ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="mr-2 h-4 w-4" />
                  )}
                  {verifyingPayment ? 'Confirming...' : 'Pay Invoice'}
                </Button>
              )}
              
              <Button
                variant="outline"
                size="lg"
                className="border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10"
                onClick={handleDownloadPdf}
                data-testid="download-pdf-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div>

            {/* Payment Terms */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-[#E8E4DE]/50 text-sm">
                Payment Terms: Net 14 days • Thank you for your business!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
